import { useEffect } from 'react';
import { buildPageTitle, DEFAULT_DESCRIPTION, DEFAULT_OG_IMAGE, SITE_NAME, toAbsoluteUrl } from '../lib/site';

const upsertMeta = (attribute, key, content) => {
    const existingTag = document.head.querySelector(`meta[${attribute}="${key}"]`);

    if (!content) {
        existingTag?.remove();
        return;
    }

    let tag = existingTag;

    if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute(attribute, key);
        document.head.appendChild(tag);
    }

    tag.setAttribute('content', content);
};

const upsertLink = (rel, href) => {
    const existingTag = document.head.querySelector(`link[rel="${rel}"]`);

    if (!href) {
        existingTag?.remove();
        return;
    }

    let tag = existingTag;

    if (!tag) {
        tag = document.createElement('link');
        tag.setAttribute('rel', rel);
        document.head.appendChild(tag);
    }

    tag.setAttribute('href', href);
};

const clearStructuredData = () => {
    document.head
        .querySelectorAll('script[data-seo-jsonld="true"]')
        .forEach((node) => node.remove());
};

const SEO = ({
    title,
    description = DEFAULT_DESCRIPTION,
    keywords,
    canonicalPath = '/',
    image = DEFAULT_OG_IMAGE,
    type = 'website',
    noindex = false,
    jsonLd = []
}) => {
    useEffect(() => {
        const resolvedTitle = buildPageTitle(title);
        const resolvedCanonical = toAbsoluteUrl(canonicalPath);
        const resolvedImage = toAbsoluteUrl(image);
        const resolvedStructuredData = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
        const robots = noindex
            ? 'noindex, nofollow'
            : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1';

        document.title = resolvedTitle;
        document.documentElement.lang = 'en';

        upsertMeta('name', 'description', description);
        upsertMeta('name', 'keywords', keywords);
        upsertMeta('name', 'robots', robots);
        upsertMeta('name', 'author', 'Anubhav Chaturvedi - NetHyTech');

        upsertMeta('property', 'og:type', type);
        upsertMeta('property', 'og:title', resolvedTitle);
        upsertMeta('property', 'og:description', description);
        upsertMeta('property', 'og:url', resolvedCanonical);
        upsertMeta('property', 'og:image', resolvedImage);
        upsertMeta('property', 'og:site_name', SITE_NAME);

        upsertMeta('name', 'twitter:card', 'summary_large_image');
        upsertMeta('name', 'twitter:title', resolvedTitle);
        upsertMeta('name', 'twitter:description', description);
        upsertMeta('name', 'twitter:image', resolvedImage);

        upsertLink('canonical', resolvedCanonical);

        clearStructuredData();
        resolvedStructuredData
            .filter(Boolean)
            .forEach((entry, index) => {
                const script = document.createElement('script');
                script.type = 'application/ld+json';
                script.dataset.seoJsonld = 'true';
                script.dataset.seoJsonldIndex = String(index);
                script.textContent = JSON.stringify(entry);
                document.head.appendChild(script);
            });

        return () => {
            clearStructuredData();
        };
    }, [canonicalPath, description, image, jsonLd, keywords, noindex, title, type]);

    return null;
};

export default SEO;
